import { useEffect, useState } from "react";

const SESSION_KEYS = {
  holiday: "nh-listing-filters-holiday",
  activity: "nh-listing-filters-activity",
};

const EPOCH_KEYS = {
  holiday: "nh-listing-filters-epoch-holiday",
  activity: "nh-listing-filters-epoch-activity",
};

const SEARCH_IDENTITY_KEYS = {
  holiday: [
    "continentId",
    "regionId",
    "countryId",
    "countrySlug",
    "countryName",
    "stateId",
    "cityId",
    "cityIds",
    "citySlug",
    "cityName",
    "keywords",
    "month",
    "packageIds",
  ],
  activity: [
    "countryId",
    "countrySlug",
    "countryName",
    "cityId",
    "citySlug",
    "cityName",
    "keywords",
    "activityTypeIds",
    "subTypeIds",
    "cityIds",
    "activityIds",
  ],
};

const epochs = {
  holiday: 0,
  activity: 0,
};

const epochListeners = new Set();

function canUseSession() {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

function readStoredEpoch(kind) {
  if (!canUseSession() || !EPOCH_KEYS[kind]) return 0;
  try {
    return Number(sessionStorage.getItem(EPOCH_KEYS[kind])) || 0;
  } catch {
    return 0;
  }
}

function notifyEpoch(kind, value) {
  epochListeners.forEach((listener) => listener(kind, value));
}

export function getListingSearchEpoch(kind) {
  if (!SESSION_KEYS[kind]) return 0;
  if (epochs[kind] === 0) {
    epochs[kind] = readStoredEpoch(kind);
  }
  return epochs[kind];
}

export function bumpListingSearchEpoch(kind) {
  if (!SESSION_KEYS[kind]) return 0;
  const next = getListingSearchEpoch(kind) + 1;
  epochs[kind] = next;
  if (canUseSession()) {
    try {
      sessionStorage.setItem(EPOCH_KEYS[kind], String(next));
    } catch {
      // ignore
    }
  }
  notifyEpoch(kind, next);
  return next;
}

export function subscribeListingSearchEpoch(listener) {
  epochListeners.add(listener);
  return () => epochListeners.delete(listener);
}

export function useListingSearchEpoch(kind) {
  const [epoch, setEpoch] = useState(() => getListingSearchEpoch(kind));

  useEffect(() => {
    setEpoch(getListingSearchEpoch(kind));
    return subscribeListingSearchEpoch((changedKind, value) => {
      if (changedKind === kind) setEpoch(value);
    });
  }, [kind]);

  return epoch;
}

function stableSearchValue(value) {
  if (Array.isArray(value)) {
    return value.map((item) => String(item)).sort().join(",");
  }
  if (value == null) return "";
  return String(value);
}

export function getListingSearchKey(kind, params = {}) {
  const keys = SEARCH_IDENTITY_KEYS[kind] || SEARCH_IDENTITY_KEYS.holiday;
  return keys.map((key) => `${key}:${stableSearchValue(params[key])}`).join("|");
}

export function isSameListingSearch(stored, kind, searchKey) {
  if (!stored || !searchKey) return false;
  if (stored.searchKey) return stored.searchKey === searchKey;
  return getListingSearchKey(kind, stored.query || {}) === searchKey;
}

export function loadListingFilters(kind) {
  if (!canUseSession() || !SESSION_KEYS[kind]) return null;

  try {
    const raw = sessionStorage.getItem(SESSION_KEYS[kind]);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function saveListingFilters(kind, payload) {
  if (!canUseSession() || !SESSION_KEYS[kind]) return;

  try {
    sessionStorage.setItem(SESSION_KEYS[kind], JSON.stringify(payload || {}));
  } catch {
    // ignore quota / private-mode errors
  }
}

export function clearListingFilters(kind) {
  if (!canUseSession()) return;

  try {
    if (kind && SESSION_KEYS[kind]) {
      sessionStorage.removeItem(SESSION_KEYS[kind]);
      return;
    }

    Object.values(SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

export function beginListingSearch(kind) {
  clearListingFilters(kind);
  bumpListingSearchEpoch(kind);
}

export function clearAllListingFilters() {
  clearListingFilters();
  bumpListingSearchEpoch("holiday");
  bumpListingSearchEpoch("activity");
}

export function mergePresentUrlDefaults(base = {}, urlDefaults = {}) {
  const next = { ...base };

  Object.entries(urlDefaults).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      if (value.length) next[key] = value;
      return;
    }

    if (value !== "" && value != null) {
      next[key] = value;
    }
  });

  return next;
}

export function clampFilterRange(range, min, max) {
  if (!Array.isArray(range) || range.length !== 2) return [min, max];

  const lo = Number(range[0]);
  const hi = Number(range[1]);
  if (!Number.isFinite(lo) || !Number.isFinite(hi)) return [min, max];

  return [
    Math.min(max, Math.max(min, lo)),
    Math.min(max, Math.max(min, hi)),
  ];
}

export function snapInclusiveRangeValue(raw, min, max, step = 1) {
  const value = Number(raw);
  const size = Number(step) > 0 ? Number(step) : 1;
  if (!Number.isFinite(value)) return min;
  if (value >= max) return max;
  if (value <= min) return min;
  if (value >= max - size) return max;
  const snapped = min + Math.round((value - min) / size) * size;
  if (snapped >= max) return max;
  return Math.min(max, Math.max(min, snapped));
}
